import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import '../styles/pages/_session.scss';

interface SessionStep {
  id: string;
  title: string;
  instructions: string;
  duration: number;
}

interface WellnessSession {
  id: string;
  title: string;
  description: string;
  category: string;
  duration: number;
  steps: SessionStep[];
  difficulty: string;
  videoUrl: string;
  themeColor: string;
  breathingTimer: boolean;
  showProgress: boolean;
}

const WellnessSessionDetailPage: React.FC = () => {
  const { id: sessionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user, token } = useAuth();

  const [session, setSession] = useState<WellnessSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch session details
  useEffect(() => {
    const fetchSession = async () => {
      try {
        if (!sessionId) {
          setError('Session ID not found');
          setIsLoading(false);
          return;
        }

        const TOKEN = token || localStorage.getItem('token');
        if (!TOKEN) {
          navigate('/login');
          return;
        }

        const response = await fetch(`/api/wellness-sessions/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${TOKEN}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch session details');
        }

        const data = await response.json();
        const sessionData = data.data?.session || data.session || data;
        setSession(sessionData);
      } catch (err) {
        console.error('Error fetching session:', err);
        setError(err instanceof Error ? err.message : 'Failed to load session');
        toast.error('Failed to load session details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSession();
  }, [sessionId, token, navigate]);

  const handleCompleteSession = async () => {
    try {
      const TOKEN = token || localStorage.getItem('token');
      if (!TOKEN) {
        toast.error('Authentication required');
        return;
      }

      // Mark session as complete
      const response = await fetch('/api/completed-sessions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${TOKEN}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: sessionId,
          title: session?.title,
          description: session?.description
        })
      });

      if (!response.ok) {
        throw new Error('Failed to mark session as complete');
      }

      toast.success('Session marked as complete!');
      navigate('/panel/dashboard/wellness-loop');
    } catch (err) {
      console.error('Error completing session:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to complete session');
    }
  };

  if (isLoading) {
    return (
      <div className={`session-page ${isDarkMode ? 'dark' : ''}`}>
        <div className="session-container">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Loading session details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className={`session-page ${isDarkMode ? 'dark' : ''}`}>
        <div className="session-container">
          <header className="session-header">
            <a
              className="session-back-link"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate(-1);
              }}
              title="Go back"
            >
              <span className="session-back-icon material-symbols-outlined">arrow_back</span>
            </a>
          </header>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>{error || 'Session not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  // Extract YouTube video ID from URL
  const getYoutubeEmbedUrl = (url: string) => {
    if (!url) return '';
    
    const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
    const match = url.match(youtubeRegex);
    
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    
    return url;
  };

  const embedUrl = getYoutubeEmbedUrl(session.videoUrl);

  return (
    <div className={`session-page ${isDarkMode ? 'dark' : ''}`}>
      <div className="session-container">
        <header className="session-header">
          <a
            className="session-back-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate(-1);
            }}
            title="Go back"
          >
            <span className="session-back-icon material-symbols-outlined">arrow_back</span>
          </a>
        </header>

        <main className="session-main">
          <div className="session-content-grid">
            {/* Video Section */}
            <div className="session-video-section">
              <h1 className="session-title">{session.title}</h1>
              <p className="session-description">{session.description}</p>
              
              {embedUrl && (
                <div className="session-video-wrapper">
                  <iframe
                    className="session-video"
                    src={embedUrl}
                    title="Wellness session video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}

              {!embedUrl && (
                <div className="session-no-video">
                  <p>No video available for this session</p>
                </div>
              )}
            </div>

            {/* Instructions Section */}
            <div className="session-instructions-section">
              <div className="session-instructions-card">
                <h2 className="session-instructions-title">Session Steps</h2>
                
                {session.steps && session.steps.length > 0 ? (
                  <div className="session-steps">
                    {session.steps.map((step, index) => (
                      <div key={step.id || index} className="session-step">
                        <div className="session-step-number">
                          {index + 1}
                        </div>
                        <div className="session-step-content">
                          <h3 className="session-step-title">{step.title}</h3>
                          <p className="session-step-description">{step.instructions}</p>
                          {step.duration > 0 && (
                            <p className="session-step-duration">Duration: {step.duration} minutes</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="session-no-steps">No steps available for this session</p>
                )}

                <button
                  className="session-complete-btn"
                  onClick={handleCompleteSession}
                >
                  <span className="session-complete-icon material-symbols-outlined">check_circle</span>
                  Mark Session as Complete
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default WellnessSessionDetailPage;
